'use client'

import Link from 'next/link'
import Image from 'next/image'
import { Heart, Music, Calendar, Radio } from 'lucide-react'
import { useMetrica } from 'next-yandex-metrica'

export default function SupportSection() {
  const { reachGoal } = useMetrica()
  // Donation links
  const donationLinks = [
    {
      name: 'DonationAlerts',
      url: 'https://www.donationalerts.com/r/nuvertal',
      icon: (
        <div className="h-10 max-h-10 flex items-center gap-1.5">
          <Image
            src="/assets/img/donation-alerts-logo.svg"
            alt="DonationAlerts лого"
            height={43}
            width={37}
            className="w-auto h-10 min-h-10 p-2 md:p-1.5"
          />
          <span className="relative -top-[1px] font-bold text-[14px] leading-normal uppercase text-white">
            DonationAlerts
          </span>
        </div>
      ),
      color: 'bg-gradient-to-r from-yellow-500 to-orange-500',
    },
    {
      name: 'Donate.Stream',
      url: 'https://donate.stream/nuvertal',
      icon: (
        <Image
          src="/assets/img/donate-stream-logo.svg"
          alt="Donate.Stream лого"
          height={52}
          width={392}
          className="w-auto h-10 min-h-10 p-2 md:p-2.5"
        />
      ),
      color: 'bg-gradient-to-r from-pink-500 to-rose-500',
    },
    {
      name: 'CloudTips',
      url: 'https://pay.cloudtips.ru/p/273fde77',
      icon: (
        <Image
          src="/assets/img/cloud-tips-logo.svg"
          alt="CloudTips лого"
          height={32}
          width={143}
          className="w-auto h-10 min-h-10 p-2 md:p-1.5"
        />
      ),
      color: 'bg-gradient-to-r from-blue-500 to-indigo-500',
    },
  ]

  // Pricing options
  const pricingOptions = [
    {
      title: 'Закажи трек на следующий стрим',
      description: <>Любимый трек в стиле Drum&Bass заиграет на одном из следующих эфиров!</>,
      price: '1 000 ₽',
      icon: <Music className="w-6 h-6" />,
      color: 'from-blue-600/20 to-blue-400/5',
      borderColor: 'border-blue-500/20',
      priceColor: 'text-blue-400',
    },
    {
      title: 'Услышь трек прямо в эфире',
      description: (
        <>
          Хочешь добавить драйва здесь и сейчас? <br />
          Ваш трек будет приоритетно сыгран в ближайшее время на текущем стриме с комментариями от
          Сани
        </>
      ),
      price: '2 000 ₽',
      icon: <Radio className="w-6 h-6" />,
      color: 'from-purple-600/20 to-purple-400/5',
      borderColor: 'border-purple-500/20',
      priceColor: 'text-purple-400',
    },
    {
      title: 'Закажи стрим',
      description: (
        <>
          Полноценный 2-х часовой стрим по вашему вкусу и настроению, который может транслироваться
          в закрытом формате только для вас либо открыто для всех. Выбирайте треки, заказывайте
          приветы и создавайте свою особую атмосферу вместе с Саней
        </>
      ),
      price: '20 000 ₽',
      icon: <Calendar className="w-6 h-6" />,
      color: 'from-emerald-600/20 to-emerald-400/5',
      borderColor: 'border-emerald-500/20',
      priceColor: 'text-emerald-400',
    },
  ]

  return (
    <section id="support" className="py-20 pt-12 relative overflow-hidden">
      {/* Enhanced background with animated gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-radio-dark-blue via-black to-radio-dark-blue opacity-90 z-0"></div>

      {/* Animated glow effect */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-radio-blue/5 blur-[100px] animate-pulse"></div>

      {/* Animated particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        {Array.from({ length: 30 }).map((_, i) => {
          // Use deterministic values based on index
          const width = 5 + (i % 10)
          const height = 5 + ((i + 3) % 10)
          const top = (i * 13) % 100
          const left = (i * 17) % 100
          const duration = 2 + ((i * 7) % 8)
          const delay = (i * 11) % 5

          return (
            <div
              key={i}
              className="absolute rounded-full bg-radio-blue/20 animate-pulse"
              style={{
                width: `${width}px`,
                height: `${height}px`,
                top: `${top}%`,
                left: `${left}%`,
                animationDuration: `${duration}s`,
                animationDelay: `${delay}s`,
              }}
            ></div>
          )
        })}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Enhanced header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center mb-4 relative">
              <div className="w-12 h-1 bg-radio-blue/30 rounded-full"></div>
              <div className="relative mx-4">
                <Heart className="text-radio-blue animate-pulse z-10 relative" />
                <div className="absolute inset-0 bg-radio-blue/20 rounded-full blur-md animate-ping"></div>
              </div>
              <div className="w-12 h-1 bg-radio-blue/30 rounded-full"></div>
            </div>
            <h2 className="text-3xl md:text-4xl font-display text-radio-blue mb-4 text-shadow-glow">
              Поддержка Проекта
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-radio-blue/10 via-radio-blue/50 to-radio-blue/10 mx-auto rounded-full"></div>
          </div>

          {/* Main content - Enhanced with better visuals and animated border */}
          <div className="relative">
            {/* Animated border glow - slow moving in one direction */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-radio-blue/30 via-radio-blue/10 to-radio-blue/30 rounded-xl blur-sm animate-slow-flow"></div>

            <div className="bg-gradient-to-br from-black/60 via-black/40 to-black/60 backdrop-blur-md rounded-xl p-6 md:p-8 border-0 shadow-[0_0_15px_rgba(30,174,215,0.15)] relative overflow-hidden will-change-transform hover:translate-y-0 hover:scale-100 hover:shadow-[0_0_20px_rgba(30,174,215,0.2)] transition-shadow duration-300 z-10">
              {/* Subtle glow effect */}
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-radio-blue/10 rounded-full blur-[60px]"></div>
              <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-radio-blue/10 rounded-full blur-[60px]"></div>

              <div className="space-y-4 relative z-10">
                {/* Support text - Enhanced */}
                <div className="text-center space-y-3">
                  <h3 className="text-xl md:text-2xl font-display text-white mb-2 relative inline-block">
                    <span className="relative z-10">
                      🎧 &quot;Радио Саня&quot; живёт благодаря вашей поддержке! 🎧
                    </span>
                    <span className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-transparent via-radio-blue/30 to-transparent"></span>
                  </h3>
                  <p className="text-gray-300 leading-relaxed max-w-3xl mx-auto">
                    Каждый донат помогает выпускать новые стримы, развивать шоу и продолжать вас
                    радовать.
                  </p>
                  <p className="text-gray-300 leading-relaxed max-w-3xl mx-auto">
                    Ваши донаты и тёплые слова — это топливо для новых стримов.
                  </p>
                  <div className="py-3">
                    <span className="inline-block px-6 py-3 bg-gradient-to-r from-radio-blue/20 to-radio-blue/10 rounded-full text-white font-bold text-lg shadow-inner shadow-white/5 border border-radio-blue/20">
                      <span className="text-radio-blue mr-2">💙</span>
                      Благодарим всех, кто поддерживает шоу!
                      <span className="text-radio-blue ml-2">💙</span>
                    </span>
                  </div>
                </div>

                {/* Pricing options */}
                <div className="pt-4">
                  <h3 className="text-2xl font-display text-radio-blue mb-8 text-center relative">
                    <span className="relative z-10">Хотите поучаствовать в шоу?</span>
                    <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-24 h-0.5 bg-gradient-to-r from-transparent via-radio-blue/50 to-transparent"></div>
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6">
                    {pricingOptions.map((option, index) => (
                      <div
                        key={index}
                        className={`bg-gradient-to-b ${option.color} backdrop-blur-md rounded-xl p-6 border ${option.borderColor} shadow-lg shadow-black/30 hover:shadow-xl hover:shadow-black/40 transition-shadow duration-300 flex flex-col h-full relative overflow-hidden group will-change-auto`}
                      >
                        <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                        {/* Icon */}
                        <div className="mb-4 p-3 rounded-full bg-black/30 w-fit">{option.icon}</div>

                        {/* Title */}
                        <h4 className="text-xl font-medium text-white mb-3">{option.title}</h4>

                        {/* Description */}
                        <p className="text-gray-300 text-sm flex-grow mb-6 min-h-[5.5rem] flex items-center">
                          {option.description}
                        </p>

                        {/* Price */}
                        <div className="mt-auto pt-4 border-t border-white/10">
                          <p className={`text-2xl font-bold ${option.priceColor}`}>
                            {option.price}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="text-center space-y-2 mt-8">
                    <p className="text-gray-300 leading-relaxed max-w-3xl mx-auto">
                      <strong>
                        Заказы принимаются онлайн во время стрима или оффлайн с обязательным
                        комментарием:
                      </strong>{' '}
                      артист - название трека
                    </p>
                    <p className="text-gray-300 leading-relaxed max-w-3xl mx-auto">
                      Для заказа персонального стрима свяжитесь со мной с помощью контактов внизу
                      страницы.
                    </p>
                  </div>
                </div>

                {/* Donation links - Enhanced */}
                <div className="pt-4 pb-3">
                  <h3 className="text-xl font-display text-radio-blue mb-8 text-center relative">
                    <span className="relative z-10">Поддержать проект</span>
                    <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-24 h-0.5 bg-gradient-to-r from-transparent via-radio-blue/50 to-transparent"></div>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {donationLinks.map((link, index) => (
                      <Link
                        key={index}
                        href={link.url}
                        onClick={() => reachGoal(`donation-link-${link.name}-click`)}
                        target="_blank"
                        aria-label={`Поддержать проект через ${link.name}`}
                        rel="noopener noreferrer"
                        className={`${link.color} text-white rounded-lg p-5 py-2 flex items-center justify-center gap-3 shadow-lg shadow-black/30 will-change-transform hover:translate-y-[-2px] transition-all duration-300 hover:shadow-xl relative overflow-hidden group`}
                      >
                        <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="absolute -inset-1 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-[-45deg] group-hover:animate-[shine_1.5s_ease]"></div>
                        <div className="">{link.icon}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx global>{`
        .text-shadow-glow {
          text-shadow: 0 0 10px rgba(30, 174, 215, 0.3);
        }
        @keyframes shine {
          0% {
            left: -100%;
          }
          100% {
            left: 100%;
          }
        }
        @keyframes slow-flow {
          0% {
            background-position: 0% 50%;
          }
          100% {
            background-position: 400% 50%;
          }
        }
        .animate-slow-flow {
          background-size: 400% 100%;
          animation: slow-flow 20s linear infinite;
        }
        .will-change-auto {
          will-change: auto;
        }
        .will-change-transform {
          will-change: transform;
        }
      `}</style>
    </section>
  )
}
